if (!customElements.get('localisation-popup')) {
  customElements.define(
    'localisation-popup',
    class LocalisationPopup extends ModalElement {
      constructor() {
        super();

        // Prevent popup on Shopify's robot challenge page
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled()) {
          return;
        }

        this.canClose = false;

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      get shouldLock() {
        return true;
      }

      get focusElement() {
        return this.querySelector('select');
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 3;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      get switzerlandRedirectUrl() {
        return this.getAttribute('data-switzerland-redirect-url');
      }

      get cookieName() {
        return 'concept:localisation-popup';
      }

      get pendingLanguageCookieName() {
        return 'concept:localisation-popup-lang';
      }

      get countrySelect() {
        return this.querySelector('#LocalisationPopupCountry');
      }

      get languageSelect() {
        return this.querySelector('#LocalisationPopupLanguage');
      }

      get confirmButton() {
        return this.querySelector('#LocalisationPopupConfirm');
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        // Runs on every page load, independent of whether the popup itself shows,
        // since it may be the page that just reloaded after the country form was submitted.
        this.applyPendingLanguage();

        this.populateLanguageSelect();

        this.confirmButton.addEventListener('click', this.onConfirm.bind(this));

        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
      }

      hide() {
        if (!this.canClose && !Shopify.designMode) return;

        return super.hide();
      }

      onWeglotReady(callback, attemptsLeft = 10) {
        if (window.Weglot && window.Weglot.initialized) {
          callback();
          return;
        }

        if (window.Weglot) {
          window.Weglot.on('initialized', callback);
          return;
        }

        // Weglot's embed script may not have run yet — retry briefly before giving up.
        if (attemptsLeft <= 0) return;

        setTimeout(() => this.onWeglotReady(callback, attemptsLeft - 1), 300);
      }

      populateLanguageSelect() {
        this.onWeglotReady(() => {
          const languages = window.Weglot.options.languages
            .map((language) => language.language_to)
            .concat(window.Weglot.options.language_from);

          const currentLang = window.Weglot.getCurrentLang();

          this.languageSelect.innerHTML = languages
            .map((code) => `<option value="${code}"${code === currentLang ? ' selected' : ''}>${window.Weglot.getLanguageName(code)}</option>`)
            .join('');

          this.languageSelect.disabled = false;

          // Replacing innerHTML doesn't fire a native 'change' event, but the theme's
          // is="custom-select" behavior (theme.js) listens for one to toggle the
          // [selected] attribute that drives the floating-label CSS — without this,
          // the language label never shrinks to match the country label.
          this.languageSelect.dispatchEvent(new Event('change'));
        });
      }

      applyPendingLanguage() {
        const pendingLanguage = this.getCookie(this.pendingLanguageCookieName);
        if (!pendingLanguage) return;

        this.onWeglotReady(() => {
          window.Weglot.switchTo(pendingLanguage);
          this.removeCookie(this.pendingLanguageCookieName);
        });
      }

      onConfirm() {
        const languageCode = this.languageSelect.value;
        const isSwitzerland = this.countrySelect.selectedOptions[0]?.hasAttribute('data-external-redirect');

        if (isSwitzerland) {
          this.canClose = true;
          this.hide();

          if (!this.testMode) {
            this.setCookie(this.cookieName, this.expiry);
          }

          window.location.href = this.switzerlandRedirectUrl;
          return;
        }

        if (!this.testMode) {
          this.setCookie(this.cookieName, this.expiry);
        }

        // Always set regardless of test mode — this is a short-lived technical relay to
        // survive the reload below, not a "remember the visitor's choice" cookie, so it
        // shouldn't be suppressed by testMode the way the persistent cookie is.
        // If Weglot never loaded, skip language entirely rather than trap the visitor
        // in a mandatory popup over a third-party script failure — country selection alone
        // still applies.
        if (languageCode) {
          this.setShortCookie(this.pendingLanguageCookieName, languageCode, 120);
        }

        // Full page reload — country/currency context changes, so we don't call hide() here.
        document.getElementById('LocalisationPopupCountryForm').requestSubmit();
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiryDays) {
        document.cookie = `${name}=true; max-age=${(expiryDays * 24 * 60 * 60)}; path=/`;
      }

      setShortCookie(name, value, expirySeconds) {
        document.cookie = `${name}=${value}; max-age=${expirySeconds}; path=/`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0; path=/`;
      }
    }
  );
}
